import { Link, Typography } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { Links } from "src/constants";
import type { FormSubmissionsReportRowDto } from "src/http";
import { sortDateTime } from "src/utilities";

const columnHelper = createColumnHelper<FormSubmissionsReportRowDto>();

const columnsConfig = (selectedTeamGuids: string[]) => {
  return [
    columnHelper.accessor("recipientUser.displayName", {
      id: "Person",
      enableHiding: false,
      meta: {
        flex: true,
      },
      header: () => <Typography variant="caption">PERSON</Typography>,
      cell: (props) => (
        <Link href={Links.User(props.row.original.recipientUser.userId)}>
          {props.row.original.recipientUser.displayName}
        </Link>
      ),
    }),
    columnHelper.accessor("recipientUser.email", {
      id: "Person Email",
      meta: {
        flex: true,
      },
      header: () => <Typography variant="caption">PERSON EMAIL</Typography>,
      cell: (props) => <Typography>{props.row.original.recipientUser.email?.toLocaleLowerCase()}</Typography>,
    }),
    columnHelper.accessor(
      (row) => {
        const primaryRole = row.recipientUserRoles.find((x) => x.isPrimaryRole);

        if (primaryRole) {
          return primaryRole.teamName;
        } else {
          return null;
        }
      },
      {
        id: "Primary Role Team",
        meta: {
          flex: true,
        },
        header: () => <Typography variant="caption">PRIMARY ROLE TEAM</Typography>,
        cell: (props) => {
          const primaryRole = props.row.original.recipientUserRoles.find((x) => x.isPrimaryRole);

          if (primaryRole) {
            return <Link href={Links.Team(primaryRole.teamSlug)}>{primaryRole.teamName}</Link>;
          } else {
            return null;
          }
        },
      },
    ),
    columnHelper.accessor(
      (row) => {
        if (selectedTeamGuids.length === 0) {
          return null;
        }

        const hasPrimaryRole = row.recipientUserRoles
          .filter((x) => selectedTeamGuids.includes(x.teamGuid))
          .some((x) => x.isPrimaryRole);

        return hasPrimaryRole ? "Yes" : "No";
      },
      {
        id: "Primary Role",
        meta: {
          flex: true,
        },
        header: () => <Typography variant="caption">PRIMARY ROLE</Typography>,
        cell: (props) => {
          const value = props.getValue();

          if (value == null) {
            return <Typography variant="placeholder">N/A</Typography>;
          } else {
            return <Typography>{value}</Typography>;
          }
        },
      },
    ),
    columnHelper.accessor("recipientUserPeopleLead.displayName", {
      id: "People Lead",
      meta: {
        flex: true,
      },
      header: () => <Typography variant="caption">PEOPLE LEAD</Typography>,
      cell: (props) => {
        if (!props.row.original.recipientUserPeopleLead) {
          return null;
        }

        return (
          <Link href={Links.User(props.row.original.recipientUserPeopleLead.userId)}>
            {props.row.original.recipientUserPeopleLead.displayName}
          </Link>
        );
      },
    }),
    columnHelper.accessor("recipientUserPeopleLead.email", {
      id: "People Lead Email",
      meta: {
        flex: true,
      },
      header: () => <Typography variant="caption">PEOPLE LEAD EMAIL</Typography>,
      cell: (props) => (
        <Typography>{props.row.original.recipientUserPeopleLead?.email?.toLocaleLowerCase()}</Typography>
      ),
    }),
    columnHelper.accessor("formName", {
      id: "Feedback Type",
      meta: {
        flex: true,
      },
      header: () => <Typography variant="caption">FEEDBACK TYPE</Typography>,
      cell: (props) =>
        props.row.original.formSubmissionGuid != null ? (
          <Link href={Links.UserFeedbackDetails(props.row.original.formSubmissionGuid)}>
            {props.row.original.formName}
          </Link>
        ) : (
          <Typography>{props.row.original.formName}</Typography>
        ),
    }),
    columnHelper.accessor("createdByUser.displayName", {
      id: "Sender",
      meta: {
        flex: true,
      },
      header: () => <Typography variant="caption">SENDER</Typography>,
      cell: (props) => {
        if (props.row.original.createdByUser == null) {
          return null;
        }

        return (
          <Link href={Links.User(props.row.original.createdByUser.userId)}>
            {props.row.original.createdByUser.displayName}
          </Link>
        );
      },
    }),
    columnHelper.accessor(
      (row) => {
        if (row.dateRangeStartUtc == null) {
          return "";
        }

        return DateTime.fromISO(row.dateRangeStartUtc, { zone: "utc" }).toLocal().toLocaleString(DateTime.DATE_MED);
      },
      {
        id: "Period Start",
        meta: {
          flex: true,
        },
        header: () => <Typography variant="caption">PERIOD START</Typography>,
        cell: (props) => <Typography>{props.getValue()}</Typography>,
        sortingFn: (rowA, rowB) => sortDateTime(rowA.original.dateRangeStartUtc, rowB.original.dateRangeStartUtc),
      },
    ),
    columnHelper.accessor(
      (row) => {
        if (row.dateRangeEndUtc == null) {
          return "";
        }

        return DateTime.fromISO(row.dateRangeEndUtc, { zone: "utc" }).toLocal().toLocaleString(DateTime.DATE_MED);
      },
      {
        id: "Period End",
        meta: {
          flex: true,
        },
        header: () => <Typography variant="caption">PERIOD END</Typography>,
        cell: (props) => <Typography>{props.getValue()}</Typography>,
        sortingFn: (rowA, rowB) => sortDateTime(rowA.original.dateRangeEndUtc, rowB.original.dateRangeEndUtc),
      },
    ),
    columnHelper.accessor(
      (row) => {
        if (row.createdDateTimeUtc == null) {
          return "";
        }

        return DateTime.fromISO(row.createdDateTimeUtc, { zone: "utc" }).toLocal().toLocaleString(DateTime.DATE_MED);
      },
      {
        id: "Created",
        meta: {
          flex: true,
        },
        header: () => <Typography variant="caption">CREATED</Typography>,
        cell: (props) => <Typography>{props.getValue()}</Typography>,
        sortingFn: (rowA, rowB) => sortDateTime(rowA.original.createdDateTimeUtc, rowB.original.createdDateTimeUtc),
      },
    ),
    columnHelper.accessor(
      (row) => {
        if (row.submittedDateTimeUtc == null) {
          return "";
        }

        return DateTime.fromISO(row.submittedDateTimeUtc, { zone: "utc" }).toLocal().toLocaleString(DateTime.DATE_MED);
      },
      {
        id: "Submitted",
        meta: {
          flex: true,
        },
        header: () => <Typography variant="caption">SUBMITTED</Typography>,
        cell: (props) => <Typography>{props.getValue()}</Typography>,
        sortingFn: (rowA, rowB) => sortDateTime(rowA.original.submittedDateTimeUtc, rowB.original.submittedDateTimeUtc),
      },
    ),
    columnHelper.accessor(
      (row) => {
        if (row.reviewedByRecipient == null) {
          return null;
        }

        return row.reviewedByRecipient ? "Yes" : "No";
      },
      {
        id: "Reviewed By Recipient",
        meta: {
          hideColumnMenu: true,
          flex: true,
        },
        header: () => <Typography variant="caption">REVIEWED BY RECIPIENT</Typography>,
        cell: (props) => {
          const value = props.getValue();

          if (value == null) {
            return;
          }

          return (
            <Typography variant="body2" color={value === "Yes" ? "success" : "error"}>
              {value}
            </Typography>
          );
        },
      },
    ),
    columnHelper.accessor(
      (row) => {
        if (row.reviewedInOneOnOne == null) {
          return null;
        }

        return row.reviewedInOneOnOne ? "Yes" : "No";
      },
      {
        id: "Reviewed In 1:1",
        meta: {
          hideColumnMenu: true,
          flex: true,
        },
        header: () => <Typography variant="caption">REVIEWED IN 1:1</Typography>,
        cell: (props) => {
          const value = props.getValue();

          if (value == null) {
            return;
          }

          return (
            <Typography variant="body2" color={value === "Yes" ? "success" : "error"}>
              {value}
            </Typography>
          );
        },
      },
    ),
  ];
};

export { columnsConfig };
